/**
 * Tooltip components built on Radix UI Tooltip primitives.
 *
 * @see Figma: {@link https://www.figma.com/design/hUDU4FBD3xkZXGdoFDc3mP/%F0%9F%93%90-OM-WEB-Library--Shadcn-?node-id=2819-31792}
 * @see Radix docs: {@link https://www.radix-ui.com/primitives/docs/components/tooltip}
 *
 * ## Primitives (composable)
 * - `Tooltip` — Root wrapper (includes `TooltipProvider`)
 * - `TooltipTrigger` — Element that activates the tooltip on hover/focus
 * - `TooltipContent` — Portal-rendered content panel with animations
 * - `TooltipArrow` — SVG arrow that points toward the trigger, fill matches tooltip bg
 *
 * ## Pre-built variants
 * - `InfoTooltip` — Info icon trigger with plain-text content, arrow, and border
 * - `WarningTooltip` — Warning icon trigger with plain-text content, arrow, and border
 * - `RichTooltip` — Custom trigger with rich children (headings, links, etc.), arrow, and border
 *
 * ## Placement
 * All variants accept an optional `side` prop (`'top' | 'bottom' | 'left' | 'right'`).
 * When omitted, Radix auto-positions based on available viewport space.
 *
 * ## Styling
 * - Padding: `p-xs` (12px)
 * - Arrow offset from trigger: 8px (`sideOffset={8}`)
 * - Border: `drop-shadow` using `var(--border)` token (avoids overlap with arrow)
 * - Max width: `max-w-60` (15rem / 240px)
 *
 * @example
 * ```tsx
 * // Simple info tooltip
 * <InfoTooltip content="Helpful info text" side="bottom" />
 *
 * // Rich tooltip with structured content
 * // Note: For interactive content (links, buttons), use Popover instead of Tooltip for keyboard accessibility.
 * <RichTooltip trigger={<Info className="size-m text-primary-special" />}>
 *   <span className="font-body text-label-small text-strong">Title</span>
 *   <span className="font-body text-body-small text-regular">Description with additional context</span>
 * </RichTooltip>
 *
 * // Composing with primitives directly
 * <Tooltip>
 *   <TooltipTrigger>Hover me</TooltipTrigger>
 *   <TooltipContent>
 *     Custom content here
 *     <TooltipArrow />
 *   </TooltipContent>
 * </Tooltip>
 * ```
 */

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CircleAlert, Info } from 'lucide-react';

/** SVG arrow that points toward the trigger. Renders inside TooltipContent. */
function TooltipArrow({
  className,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Arrow>) {
  return (
    <TooltipPrimitive.Arrow
      data-slot="tooltip-arrow"
      className={`fill-popover ${className ?? ''}`}
      {...props}
    />
  );
}

/** Configures delay and skip behaviour for nested tooltips. Wrapped automatically by `Tooltip`. */
function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

/** Root tooltip wrapper. Automatically includes a `TooltipProvider` with zero delay. */
function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

/** Element that activates the tooltip on hover or focus. Renders as a `<button>`. */
function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      type="button"
      {...props}
    />
  );
}

/** Portal-rendered tooltip content panel with entrance/exit animations. */
function TooltipContent({
  className,
  sideOffset = 8,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={`z-50 w-fit text-balance rounded-md bg-popover p-xs text-xs text-popover-foreground animate-in fade-in-0 zoom-in-100 [filter:drop-shadow(0_0_1px_var(--border))_drop-shadow(0_1px_3px_rgb(0_0_0/0.08))] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-100 data-[side=bottom]:slide-in-from-top-xxs data-[side=left]:slide-in-from-right-xxs data-[side=right]:slide-in-from-left-xxs data-[side=top]:slide-in-from-bottom-xxs ${className ?? ''}`}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

type TooltipVariantProps = {
  content: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
} & Omit<React.ComponentProps<typeof TooltipPrimitive.Root>, 'children'>;

/**
 * Pre-built tooltip with a warning (CircleAlert) icon trigger and plain-text content.
 * Includes arrow, border, and 12px padding.
 *
 * @param content - Plain text string displayed inside the tooltip
 * @param side - Optional placement: 'top' | 'bottom' | 'left' | 'right'. Defaults to auto.
 */
function WarningTooltip({ content, side, ...props }: TooltipVariantProps) {
  return (
    <Tooltip {...props}>
      <TooltipTrigger>
        <CircleAlert className="size-m text-warning-foreground" />
      </TooltipTrigger>
      <TooltipContent className="max-w-60" side={side}>
        <span className="block w-auto whitespace-pre-wrap text-wrap font-body text-body-small text-regular">
          {content}
        </span>
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Pre-built tooltip with an info (Info) icon trigger and plain-text content.
 * Includes arrow, border, and 12px padding.
 *
 * @param content - Plain text string displayed inside the tooltip
 * @param side - Optional placement: 'top' | 'bottom' | 'left' | 'right'. Defaults to auto.
 */
function InfoTooltip({ content, side, ...props }: TooltipVariantProps) {
  return (
    <Tooltip {...props}>
      <TooltipTrigger>
        <Info className="size-m text-primary-special" />
      </TooltipTrigger>
      <TooltipContent className="max-w-60" side={side}>
        <span className="block w-auto whitespace-pre-wrap text-wrap font-body text-body-small text-regular">
          {content}
        </span>
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
}

type RichTooltipProps = {
  trigger: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  defaultOpen?: boolean;
};

/**
 * Composable popover-based tooltip for rich/interactive content (headings, links, buttons).
 * Uses Radix Popover internally for keyboard accessibility (content is focusable and tabbable).
 * Includes arrow, border, and 12px padding. Content is laid out in a vertical flex column.
 *
 * Use this instead of Tooltip when content contains interactive elements.
 *
 * @param trigger - React node rendered as the click target (e.g. an icon)
 * @param children - Rich content rendered inside the popover body
 * @param side - Optional placement: 'top' | 'bottom' | 'left' | 'right'. Defaults to auto.
 * @param align - Optional alignment: 'start' | 'center' | 'end'. Defaults to 'start'.
 * @param className - Optional additional classes on the content container
 */
function RichTooltip({
  trigger,
  children,
  className,
  side,
  align = 'start',
  defaultOpen,
}: RichTooltipProps) {
  return (
    <PopoverPrimitive.Root defaultOpen={defaultOpen}>
      <PopoverPrimitive.Trigger data-slot="rich-tooltip-trigger" asChild>
        <button type="button">{trigger}</button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          data-slot="rich-tooltip-content"
          side={side}
          align={align}
          sideOffset={8}
          className={`z-50 w-auto max-w-60 rounded-md bg-popover p-xs text-popover-foreground animate-in fade-in-0 zoom-in-100 [filter:drop-shadow(0_0_1px_var(--border))_drop-shadow(0_1px_3px_rgb(0_0_0/0.08))] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-100 data-[side=bottom]:slide-in-from-top-xxs data-[side=left]:slide-in-from-right-xxs data-[side=right]:slide-in-from-left-xxs data-[side=top]:slide-in-from-bottom-xxs ${className ?? ''}`}
        >
          <div className="flex flex-col gap-xxs">{children}</div>
          <PopoverPrimitive.Arrow className="fill-popover" />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipArrow,
  WarningTooltip,
  InfoTooltip,
  RichTooltip,
};
